import { useAuth0 } from '@auth0/auth0-react';
import { UseAuthRoles } from './UseAuthRoles';

// Get the API base URL from environment variables or use a default
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://inkedupmanager.com/api';

export function UseApi() {
  const { getAccessTokenSilently } = useAuth0();
  const authRoles = UseAuthRoles();

  // Feature access checker
  const hasFeatureAccess = (featureTier) => {
    const userTier = authRoles.getSubscriptionLevel();
    
    if (userTier === 'premium') return true;
    if (userTier === 'standard' && featureTier !== 'premium') return true;
    if (userTier === 'basic' && featureTier === 'basic') return true;
    return false;
  };

  const fetchWithAuth = async (url, options = {}) => {
    try {
      // Get the token
      const token = await getAccessTokenSilently({
        audience: "https://inkedupmanager.com/api",
      });
      
      // Ensure the URL is fully formed
      const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;
      
      // Check if this is a FormData request
      const isFormData = options.body instanceof FormData;
      
      // Add authorization header to the provided options
      const authOptions = {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
          // Only set Content-Type if it's not FormData
          ...(isFormData ? {} : { 'Content-Type': options.headers?.['Content-Type'] || 'application/json' }),
        },
      };

      // IMPORTANT: For FormData, remove all headers except Authorization  
      if (isFormData) {
        authOptions.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      
      // Make the API request
      const response = await fetch(fullUrl, authOptions);
      
      if (!response.ok) {
        let errorMessage;
        try {
          // FIXED: Clone the response before reading to avoid "body already consumed" error
          const errorResponse = response.clone();
          const errorData = await errorResponse.json();
          errorMessage = errorData.detail || errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          try {
            // Try to read as text from the original response
            const textError = await response.text();
            errorMessage = textError || 'Unknown error';
          } catch (textError) {
            errorMessage = `HTTP ${response.status} error`;
          }
        }
        console.error('API error:', response.status, errorMessage);
        throw new Error(`API error: ${response.status} ${errorMessage}`);
      }
      
      // Handle different response types based on content-type header
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('image/')) {
        const blob = await response.blob();
        return blob;
      } else if (contentType && contentType.includes('application/json')) {
        try {
          const data = await response.json();
          return data || []; 
        } catch (e) {
          console.warn('Could not parse JSON response:', e);
          return []; 
        }
      } else {
        try {
          const text = await response.text();
          if (!text) return { success: true };
          try {
            return JSON.parse(text);
          } catch (e) {
            return text;
          }
        } catch (e) {
          console.warn('Error processing response:', e);
          return { success: true };
        }
      }
    } catch (error) {
      console.error('Request error:', error);
      throw error;
    }
  };

  // FIXED: Client methods with proper response handling
  const fetchClients = async () => {
    try {
      const data = await fetchWithAuth('/clients/');
      // Handle the backend response structure correctly
      // Backend returns {clients: [...], total: X, view_mode: "all"}
      if (data && data.clients && Array.isArray(data.clients)) {
        return data.clients;
      } else if (Array.isArray(data)) {
        // Fallback: if it's already an array (for backwards compatibility)
        return data;
      } else {
        // Fallback: return empty array if unexpected structure
        console.warn('⚠️ UseApi: Unexpected fetchClients response structure:', data);
        return [];
      }
    } catch (error) {
      console.error('❌ UseApi: Error in fetchClients:', error);
      throw error;
    }
  };

  const addClient = async (clientData) => {
    try {
      return await fetchWithAuth('/clients/add/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(clientData)
      });
    } catch (error) {
      console.error('Error adding client:', error);
      throw error;
    }
  };

  const fetchClient = async (clientId) => {
    try {
      return await fetchWithAuth(`/clients/${clientId}/`);
    } catch (error) {
      console.error(`Error fetching client ${clientId}:`, error);
      throw error;
    }
  };

  const updateClient = async (clientId, clientData) => {
    try {
      // Ensure we're only sending client data, not image data
      const cleanClientData = { ...clientData };
      
      // Remove any image-related fields that shouldn't be in regular updates
      delete cleanClientData.client_id_image;
      delete cleanClientData.client_id_image_url;
      
      return await fetchWithAuth(`/clients/${clientId}/update/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanClientData)
      });
    } catch (error) {
      console.error(`Error updating client ${clientId}:`, error);
      throw error;
    }
  };

  const deleteClient = async (clientId) => {
    try {
      return await fetchWithAuth(`/clients/${clientId}/delete/`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error(`Error deleting client ${clientId}:`, error);
      throw error;
    }
  };

  // Image handling methods - UPDATED FOR S3 PRE-SIGNED URLs
  const fetchClientImage = async (clientId) => {
    try {
      const response = await fetchWithAuth(`/clients/${clientId}/secure-id/`);
      // Backend now returns JSON with pre-signed S3 URL
      if (response && response.image_url) {
        return response.image_url;
      } else {
        console.error("Unexpected response format for image:", response);
        throw new Error("Failed to load image: Invalid response format");
      }
    } catch (error) {
      console.error(`Error fetching client image for ${clientId}:`, error);
      throw error;
    }
  };

  const uploadClientImage = async (clientId, imageFile) => {
    try {
      const formData = new FormData();
      formData.append("client_id_image", imageFile);
      return await fetchWithAuth(`/clients/${clientId}/upload-id/`, {
        method: 'POST',
        body: formData
        // No headers property at all for FormData
      });
    } catch (error) {
      console.error(`Error uploading client image for ${clientId}:`, error);
      throw error;
    }
  };

  const removeClientImage = async (clientId) => {
    try {
      return await fetchWithAuth(`/clients/${clientId}/remove-id/`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error(`Error removing client image for ${clientId}:`, error);
      throw error;
    }
  };

  // Tattoo History methods
  const fetchClientTattooHistory = async (clientId) => {
    try {
      const data = await fetchWithAuth(`/clients/${clientId}/tattoo-history/`);
      return data || [];
    } catch (error) {
      console.error(`Error fetching tattoo history for client ${clientId}:`, error);
      throw error;
    }
  };

  const uploadClientTattooImage = async (clientId, file, tattooData) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('description', tattooData.description);
      formData.append('date', tattooData.date);
      formData.append('location', tattooData.location);
      return await fetchWithAuth(`/clients/${clientId}/upload-tattoo-image/`, {
        method: 'POST',
        body: formData
        // No headers property at all for FormData
      });
    } catch (error) {
      console.error(`Error uploading tattoo image for client ${clientId}:`, error);
      throw error;
    }
  };

  const updateClientTattooImage = async (clientId, tattooId, tattooData) => {
    try {
      return await fetchWithAuth(`/clients/${clientId}/tattoo-images/${tattooId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tattooData)
      });
    } catch (error) {
      console.error(`Error updating tattoo image ${tattooId} for client ${clientId}:`, error);
      throw error;
    }
  };

  const deleteClientTattooImage = async (clientId, tattooId) => {
    try {
      return await fetchWithAuth(`/clients/${clientId}/tattoo-images/${tattooId}/`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error(`Error deleting tattoo image ${tattooId} for client ${clientId}:`, error);
      throw error;
    }
  };

  // UPDATED FOR S3 PRE-SIGNED URLs
  const fetchTattooImage = async (clientId, tattooId) => {
    try {
      const response = await fetchWithAuth(`/tattoo-images/${clientId}/${tattooId}/`);
      // Backend now returns JSON with pre-signed S3 URL
      if (response && response.image_url) {
        return response.image_url;
      } else {
        console.error("Unexpected response format for tattoo image:", response);
        throw new Error("Failed to load tattoo image: Invalid response format");
      }
    } catch (error) {
      console.error(`Error fetching tattoo image ${tattooId} for client ${clientId}:`, error);
      throw error;
    }
  };

  // Appointment methods
  const fetchAppointments = async () => {
    try {
      return await fetchWithAuth('/appointments/');
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  };

  const addAppointment = async (appointmentData) => {
    try {
      return await fetchWithAuth('/appointments/create/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData)
      });
    } catch (error) {
      console.error('Error adding appointment:', error);
      throw error;
    }
  };

  // Basic Inventory methods (available to ALL users)
  const fetchInventory = async () => {
    try {
      return await fetchWithAuth('/inventory/');
    } catch (error) {
      console.error('Error fetching inventory:', error);
      throw error;
    }
  };

  const addInventoryItem = async (itemData) => {
    try {
      return await fetchWithAuth('/inventory/add/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemData)
      });
    } catch (error) {
      console.error('Error adding inventory item:', error);
      throw error;
    }
  };

  const updateInventoryItem = async (itemId, itemData) => {
    try {
      return await fetchWithAuth(`/inventory/${itemId}/update/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemData)
      });
    } catch (error) {
      console.error(`Error updating inventory item ${itemId}:`, error);
      throw error;
    }
  };

  const deleteInventoryItem = async (itemId) => {
    try {
      return await fetchWithAuth(`/inventory/${itemId}/delete/`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error(`Error deleting inventory item ${itemId}:`, error);
      throw error;
    }
  };

  // Advanced Tattoo Studio Inventory methods (Standard/Premium only)
  const fetchTattooInventoryOverview = async () => {
    try {
      return await fetchWithAuth('/inventory/tattoo/overview/');
    } catch (error) {
      console.error('Error fetching tattoo inventory overview:', error);
      throw error;
    }
  };

  const fetchTattooInventory = async () => {
    try {
      return await fetchWithAuth('/inventory/tattoo/');
    } catch (error) {
      console.error('Error fetching tattoo inventory:', error);
      throw error;
    }
  };

  const addTattooSupply = async (supplyData) => {
    try {
      return await fetchWithAuth('/inventory/tattoo/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(supplyData)
      });
    } catch (error) {
      console.error('Error adding tattoo supply:', error);
      throw error;
    }
  };

  const logSupplyUsage = async (usageData) => {
    try {
      return await fetchWithAuth('/inventory/tattoo/log-usage/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(usageData)
      });
    } catch (error) {
      console.error('Error logging supply usage:', error);
      throw error;
    }
  };

  const restockSupply = async (restockData) => {
    try {
      return await fetchWithAuth('/inventory/tattoo/restock/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(restockData)
      });
    } catch (error) {
      console.error('Error restocking supply:', error);
      throw error;
    }
  };

  // Add the sendAftercareEmail method
  const sendAftercareEmail = async (clientId, emailData) => {
    try {
      return await fetchWithAuth(`/clients/${clientId}/send-aftercare-email/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });
    } catch (error) {
      console.error(`Error sending aftercare email for client ${clientId}:`, error);
      throw error;
    }
  };

  // ===== CUSTOM FORM METHODS =====
  
  // Custom form template methods
  const fetchCustomFormTemplates = async () => {
    try {
      const response = await fetchWithAuth('/custom-forms/');
      // Handle new response format: {templates: [...], total: X}
      if (response && response.templates) {
        return response.templates;
      }
      
      // Handle old format or direct array
      return response || [];
    } catch (error) {
      console.error('Error fetching custom form templates:', error);
      throw error;
    }
  };

  const createCustomFormTemplate = async (templateData) => {
    try {
      return await fetchWithAuth('/custom-forms/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(templateData)
      });
    } catch (error) {
      console.error('Error creating custom form template:', error);
      throw error;
    }
  };

  const updateCustomFormTemplate = async (templateId, templateData) => {
    try {
      return await fetchWithAuth(`/custom-forms/${templateId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(templateData)
      });
    } catch (error) {
      console.error(`Error updating custom form template ${templateId}:`, error);
      throw error;
    }
  };

  const deleteCustomFormTemplate = async (templateId) => {
    try {
      return await fetchWithAuth(`/custom-forms/${templateId}/`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error(`Error deleting custom form template ${templateId}:`, error);
      throw error;
    }
  };

  const getCustomFormTemplate = async (templateId) => {
    try {
      return await fetchWithAuth(`/custom-forms/${templateId}/`);
    } catch (error) {
      console.error(`Error fetching custom form template ${templateId}:`, error);
      throw error;
    }
  };

  // Custom form submission methods with enhanced legal compliance
  const submitCustomForm = async (submissionData) => {
    try {
      return await fetchWithAuth('/custom-forms/submit/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });
    } catch (error) {
      console.error('Error submitting custom form:', error);
      throw error;
    }
  };

  const fetchCustomFormSubmissions = async (templateId = null) => {
    try {
      const url = templateId 
        ? `/custom-forms/submissions/?template_id=${templateId}`
        : '/custom-forms/submissions/';
      const response = await fetchWithAuth(url);
      // Handle new response format: {submissions: [...], total: X}
      if (response && response.submissions) {
        return response.submissions;
      }
      
      // Handle old format or direct array
      return response || [];
    } catch (error) {
      console.error('Error fetching custom form submissions:', error);
      throw error;
    }
  };

  const getCustomFormSubmission = async (submissionId) => {
    try {
      return await fetchWithAuth(`/custom-forms/submissions/${submissionId}/`);
    } catch (error) {
      console.error(`Error fetching custom form submission ${submissionId}:`, error);
      throw error;
    }
  };

  const uploadCustomFormImage = async (submissionId, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      return await fetchWithAuth(`/custom-forms/submissions/${submissionId}/upload/`, {
        method: 'POST',
        body: formData
        // No headers property at all for FormData
      });
    } catch (error) {
      console.error(`Error uploading custom form image for submission ${submissionId}:`, error);
      throw error;
    }
  };

  // Custom form image serving - UPDATED FOR S3 PRE-SIGNED URLs
  const fetchCustomFormImage = async (imageId) => {
    try {
      const response = await fetchWithAuth(`/custom-form-images/${imageId}/`);
      // Backend now returns JSON with pre-signed S3 URL
      if (response && response.image_url) {
        return response.image_url;
      } else {
        console.error("Unexpected response format for custom form image:", response);
        throw new Error("Failed to load custom form image: Invalid response format");
      }
    } catch (error) {
      console.error(`Error fetching custom form image ${imageId}:`, error);
      throw error;
    }
  };

  // Enhanced PDF Generation for custom forms with legal compliance
  const generateCustomFormPDF = async (submissionId) => {
    try {
      const blob = await fetchWithAuth(`/custom-forms/submissions/${submissionId}/pdf/`, {
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      if (blob instanceof Blob) {
        // Create download link
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `custom_form_submission_${submissionId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return { success: true };
      } else {
        throw new Error("Failed to generate PDF: Invalid response format");
      }
    } catch (error) {
      console.error(`Error generating custom form PDF for submission ${submissionId}:`, error);
      throw error;
    }
  };

  // Template sharing method for studio owners
  const toggleTemplateSharing = async (templateId, shareWithStudio) => {
    try {
      return await fetchWithAuth(`/custom-forms/${templateId}/toggle-sharing/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ share_with_studio: shareWithStudio })
      });
    } catch (error) {
      console.error(`Error toggling template sharing for ${templateId}:`, error);
      throw error;
    }
  };

  // ===== SHOP/STUDIO INFO METHODS =====
  
  // Fetch shop information for dynamic e-signature disclosures
  const fetchShopInfo = async () => {
    try {
      return await fetchWithAuth('/shop/profile/');
    } catch (error) {
      console.error('Error fetching shop info:', error);
      throw error;
    }
  };

  const updateShopInfo = async (shopData) => {
    try {
      return await fetchWithAuth('/shop/profile/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(shopData)
      });
    } catch (error) {
      console.error('Error updating shop info:', error);
      throw error;
    }
  };

  // ===== STUDIO MANAGEMENT METHODS =====
  
  // Studio invitation completion method
  const completeStudioInvitation = async (invitationToken) => {
    try {
      return await fetchWithAuth('/studio/complete-invite/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invitation_token: invitationToken
        })
      });
    } catch (error) {
      console.error('Error completing studio invitation:', error);
      throw error;
    }
  };

  // Studio members management
  const fetchStudioMembers = async () => {
    try {
      const data = await fetchWithAuth('/studio/info/');
      return data || {};
    } catch (error) {
      console.error('Error fetching studio members:', error);
      throw error;
    }
  };

  const sendStudioInvitation = async (email) => {
    try {
      return await fetchWithAuth('/studio/invite/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });
    } catch (error) {
      console.error('Error sending studio invitation:', error);
      throw error;
    }
  };

  // ===== FIXED: PUBLIC PROFILE MANAGEMENT METHODS =====
  
  // Fetch public profile settings - FIXED: Correct endpoint
  const fetchPublicProfileSettings = async () => {
    try {
      // FIXED: Your backend uses '/public-profile-settings/' not '/public-profile/settings/'
      return await fetchWithAuth('/public-profile-settings/');
    } catch (error) {
      console.error('Error fetching public profile settings:', error);
      throw error;
    }
  };

  // Update public profile settings - FIXED: Correct endpoint
  const updatePublicProfileSettings = async (profileData) => {
    try {
      // FIXED: Your backend uses '/public-profile-settings/' not '/public-profile/settings/'
      return await fetchWithAuth('/public-profile-settings/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData)
      });
    } catch (error) {
      console.error('Error updating public profile settings:', error);
      throw error;
    }
  };

  // Upload profile image - FIXED: Updated endpoint
  const uploadProfileImage = async (imageFile) => {
    try {
      const formData = new FormData();
      formData.append('profile_image', imageFile);
      // Updated endpoint - you may need to create this endpoint or modify the existing one
      return await fetchWithAuth('/profile-image/upload/', {
        method: 'POST',
        body: formData
      });
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw error;
    }
  };

  // ===== FIXED: PORTFOLIO MANAGEMENT METHODS =====
  
  // ✅ FIXED: Fetch artist portfolio items - using correct endpoint
  const fetchPortfolioItems = async () => {
    try {
      const response = await fetchWithAuth('/artist/portfolio/images/');
      
      // Handle response format: {images: [...], total: X}
      if (response && response.images) {
        return response.images;
      }
      
      // Handle old format or direct array
      return response || [];
    } catch (error) {
      console.error('Error fetching portfolio items:', error);
      throw error;
    }
  };

  // ✅ FIXED: Add artist portfolio item - using correct endpoint
  const addPortfolioItem = async (itemData) => {
    try {
      return await fetchWithAuth('/artist/portfolio/images/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemData)
      });
    } catch (error) {
      console.error('Error adding portfolio item:', error);
      throw error;
    }
  };

  // ✅ FIXED: Update artist portfolio item - using correct endpoint
  const updatePortfolioItem = async (itemId, itemData) => {
    try {
      return await fetchWithAuth(`/artist/portfolio/images/${itemId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemData)
      });
    } catch (error) {
      console.error(`Error updating portfolio item ${itemId}:`, error);
      throw error;
    }
  };

  // ✅ FIXED: Delete artist portfolio item - using correct endpoint
  const deletePortfolioItem = async (itemId) => {
    try {
      return await fetchWithAuth(`/artist/portfolio/images/${itemId}/`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error(`Error deleting portfolio item ${itemId}:`, error);
      throw error;
    }
  };

  // ===== STUDIO PORTFOLIO METHODS =====
  
  // Fetch studio portfolio items
  const fetchStudioPortfolioItems = async () => {
    try {
      const response = await fetchWithAuth('/studio/portfolio/images/');
      
      // Handle response format: {images: [...], total: X}
      if (response && response.images) {
        return response.images;
      }
      
      // Handle old format or direct array
      return response || [];
    } catch (error) {
      console.error('Error fetching studio portfolio items:', error);
      throw error;
    }
  };

  // Add studio portfolio item
  const addStudioPortfolioItem = async (itemData) => {
    try {
      return await fetchWithAuth('/studio/portfolio/images/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemData)
      });
    } catch (error) {
      console.error('Error adding studio portfolio item:', error);
      throw error;
    }
  };

  // Update studio portfolio item
  const updateStudioPortfolioItem = async (itemId, itemData) => {
    try {
      return await fetchWithAuth(`/studio/portfolio/images/${itemId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemData)
      });
    } catch (error) {
      console.error(`Error updating studio portfolio item ${itemId}:`, error);
      throw error;
    }
  };

  // Delete studio portfolio item
  const deleteStudioPortfolioItem = async (itemId) => {
    try {
      return await fetchWithAuth(`/studio/portfolio/images/${itemId}/`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error(`Error deleting studio portfolio item ${itemId}:`, error);
      throw error;
    }
  };

  // ===== FIXED: CONTACT INQUIRIES METHODS =====
  
  // ✅ FIXED: Fetch contact inquiries - using correct endpoint
  const fetchContactInquiries = async () => {
    try {
      const response = await fetchWithAuth('/contact/inquiries/');
      
      // Handle response format: {inquiries: [...], total: X}
      if (response && response.inquiries) {
        return response.inquiries;
      }
      
      // Handle old format or direct array
      return response || [];
    } catch (error) {
      console.error('Error fetching contact inquiries:', error);
      throw error;
    }
  };

  // Mark inquiry as read
  const markInquiryAsRead = async (inquiryId) => {
    try {
      return await fetchWithAuth(`/contact/inquiries/${inquiryId}/mark-read/`, {
        method: 'POST'
      });
    } catch (error) {
      console.error(`Error marking inquiry ${inquiryId} as read:`, error);
      throw error;
    }
  };

  // Delete inquiry
  const deleteInquiry = async (inquiryId) => {
    try {
      return await fetchWithAuth(`/contact/inquiries/${inquiryId}/`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error(`Error deleting inquiry ${inquiryId}:`, error);
      throw error;
    }
  };

  // Respond to inquiry
  const respondToInquiry = async (inquiryId, responseData) => {
    try {
      return await fetchWithAuth(`/contact/inquiries/${inquiryId}/respond/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(responseData)
      });
    } catch (error) {
      console.error(`Error responding to inquiry ${inquiryId}:`, error);
      throw error;
    }
  };

  // ===== SHOP PROFILE METHODS =====
  
  // Fetch shop profile (different from shop info - this is for public display)
  const fetchShopProfile = async () => {
    try {
      return await fetchWithAuth('/shop/profile/');
    } catch (error) {
      console.error('Error fetching shop profile:', error);
      throw error;
    }
  };

  // Update shop profile
  const updateShopProfile = async (profileData) => {
    try {
      return await fetchWithAuth('/shop/profile/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData)
      });
    } catch (error) {
      console.error('Error updating shop profile:', error);
      throw error;
    }
  };

  // Return all API methods
  return {
    fetchWithAuth,
    hasFeatureAccess,
    // Client methods
    fetchClients,
    fetchClient,
    addClient,
    updateClient,
    deleteClient,
    // Image handling methods
    fetchClientImage,
    uploadClientImage,
    removeClientImage,
    // Tattoo History methods
    fetchClientTattooHistory,
    uploadClientTattooImage,
    updateClientTattooImage,
    deleteClientTattooImage,
    fetchTattooImage,
    // Appointment methods
    fetchAppointments,
    addAppointment,
    // Basic Inventory methods (available to ALL users)
    fetchInventory,
    addInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    // Advanced Tattoo Studio Inventory methods (Standard/Premium only)
    fetchTattooInventoryOverview,
    fetchTattooInventory,
    addTattooSupply,
    logSupplyUsage,
    restockSupply,
    // Aftercare email method
    sendAftercareEmail,
    // Custom form methods
    fetchCustomFormTemplates,
    createCustomFormTemplate,
    updateCustomFormTemplate,
    deleteCustomFormTemplate,
    getCustomFormTemplate,
    submitCustomForm,
    fetchCustomFormSubmissions,
    getCustomFormSubmission,
    uploadCustomFormImage,
    fetchCustomFormImage,
    generateCustomFormPDF,
    toggleTemplateSharing,
    // Shop/Studio info methods
    fetchShopInfo,
    updateShopInfo,
    // Studio management methods
    completeStudioInvitation,
    fetchStudioMembers,
    sendStudioInvitation,
    // ✅ FIXED: Public profile management methods
    fetchPublicProfileSettings,
    updatePublicProfileSettings,
    uploadProfileImage,
    // ✅ FIXED: Portfolio management methods (Artist)
    fetchPortfolioItems,
    addPortfolioItem,
    updatePortfolioItem,
    deletePortfolioItem,
    // NEW: Studio portfolio management methods
    fetchStudioPortfolioItems,
    addStudioPortfolioItem,
    updateStudioPortfolioItem,
    deleteStudioPortfolioItem,
    // ✅ FIXED: Contact inquiries methods
    fetchContactInquiries,
    markInquiryAsRead,
    deleteInquiry,
    respondToInquiry,
    // Shop profile methods
    fetchShopProfile,
    updateShopProfile
  };
}

export default UseApi;